import React from "react";
import { flushSync } from "react-dom";
import { C } from "../../constants/design";
import { AuraType, Power, LeagueCardState, LeagueMatchRecord } from "../../types/game";
import { EFFECT_ICONS } from "../../constants/league";
import { GK_UPGRADE_COST } from "../../engine/leagueEngine";
import { ComboActivation } from "../../engine/shopEngine";
import {
  CARD_BY_ID,
  CardDef,
  CardFamily,
  TIER_META,
  REROLL_COST,
  ORB_COST,
  MAX_INVENTORY,
  SELL_REFUND,
} from "../../constants/cards";
import { ScheduleEntry, FrozenFixture } from "../../hooks/useLeague";
import { MatchSimResult } from "../../engine/matchEngine";
import { powerDesc } from "../../constants/crafting";
import { MatchSheetPage } from "./MatchSheetPage";
import { OrbIcon } from "./OrbIcon";
import {
  ShopCard,
  ShopCardPlaceholder,
  CardModal,
  FamilyModal,
  FamilyDots,
  LockIcon,
} from "./CardVisuals";

interface LeaguePrepProps {
  // ── Cartes / boutique ──
  leagueGold: number;
  cardState: LeagueCardState;
  teamCombos: ComboActivation[];
  onBuy: (cardId: string) => void;
  onSell: (cardId: string) => void;
  onBuyOrb: (type: AuraType) => boolean;
  onReroll: () => void;
  onSendToField: (cardId: string) => void;
  onSetLocked: (locked: boolean) => void;
  onToast: (msg: string) => void;
  // ── Boost (orbes / pouvoirs) ──
  orbCounts: Record<AuraType, number>;
  powers: Power[];
  onFusion: () => void;
  // ── Matchs ──
  schedule: ScheduleEntry[];
  myTeamPower: number;
  resultByRound: Record<number, "win" | "draw" | "loss">;
  scoreByRound: Record<number, { gf: number; ga: number }>;
  fixtureByRound: Record<number, FrozenFixture>;
  isTester?: boolean;
  runTestMatch?: (powerId?: string) => MatchSimResult;
  matchHistory: LeagueMatchRecord[];
  currentMatch: LeagueMatchRecord | null;
  matchPowers: Record<number, string>;
  assignPowerToMatch: (matchIndex: number, powerId: string) => void;
  unassignMatch: (matchIndex: number) => void;
  // ── Gardien ──
  goalkeeperLevel: number;
  upgradeGoalkeeper: (amount: number) => Promise<void>;
  wallet: number;
  onBack: () => void;
}

const ALL_AURA_TYPES: AuraType[] = ["ROUGE", "BLEUE", "VERTE"];

const sectionTitle: React.CSSProperties = {
  fontSize: 16,
  fontWeight: 900,
  margin: "0 0 10px",
};

// Compte à rebours jusqu'à la prochaine rotation (minuit local).
function ResetCountdown({ locked }: { locked: boolean }) {
  const compute = () => {
    const now = new Date();
    const midnight = new Date(now);
    midnight.setHours(24, 0, 0, 0);
    const diff = midnight.getTime() - now.getTime();
    const h = Math.floor(diff / 3600000);
    const m = Math.floor((diff % 3600000) / 60000);
    if (h > 0) return `${h}h ${m} min`;
    return `${m} minute${m > 1 ? "s" : ""}`;
  };
  const [label, setLabel] = React.useState(compute);
  React.useEffect(() => {
    const id = setInterval(() => setLabel(compute()), 30_000);
    return () => clearInterval(id);
  }, []);
  if (locked) {
    return (
      <div style={{ fontSize: 11.5, color: C.turf, marginTop: 2, fontWeight: 700 }}>
        🔒 Locked until tomorrow
      </div>
    );
  }
  return (
    <div style={{ fontSize: 11.5, color: C.muted, marginTop: 2 }}>
      Rotation resets in {label}
    </div>
  );
}

export function LeaguePrep({
  leagueGold,
  cardState,
  teamCombos,
  onBuy,
  onSell,
  onBuyOrb,
  onReroll,
  onSendToField,
  onSetLocked,
  onToast,
  orbCounts,
  powers,
  onFusion,
  schedule,
  myTeamPower,
  resultByRound,
  scoreByRound,
  fixtureByRound,
  isTester,
  runTestMatch,
  matchPowers,
  assignPowerToMatch,
  unassignMatch,
  goalkeeperLevel,
  upgradeGoalkeeper,
  wallet,
  onBack,
}: LeaguePrepProps) {
  const [inspect, setInspect] = React.useState<{
    card: CardDef;
    buyable: boolean;
  } | null>(null);
  const [orbInspect, setOrbInspect] = React.useState<AuraType | null>(null);
  const [familyModal, setFamilyModal] = React.useState<CardFamily | null>(null);
  const [inspectPower, setInspectPower] = React.useState<Power | null>(null);
  const [matchFilter, setMatchFilter] = React.useState<"upcoming" | "done">(
    "upcoming"
  );
  const [pickerFor, setPickerFor] = React.useState<number | null>(null);
  const [detailFor, setDetailFor] = React.useState<number | null>(null);
  const [sheetFor, setSheetFor] = React.useState<number | null>(null);
  const [testOpen, setTestOpen] = React.useState(false);
  const [gkUpgrading, setGkUpgrading] = React.useState(false);

  const { shop, inventory, active, locked } = cardState;
  const myCard = active[0] ?? null;

  const businessClubFree =
    teamCombos.some((c) => c.family === "vip" && c.tier2) &&
    shop.rerollsUsed === 0;
  const rerollCost = businessClubFree ? 0 : REROLL_COST;

  const ownedIds = Object.keys(inventory).filter((id) => inventory[id] > 0);
  const invCount = Object.values(inventory).reduce((a, b) => a + b, 0);
  const invFull = invCount >= MAX_INVENTORY;
  // Les orbes vont dans les auras (pas l'inventaire de cartes) → non plafonnées.
  const ORB_LABEL: Record<AuraType, string> = {
    ROUGE: "Red Orb",
    BLEUE: "Blue Orb",
    VERTE: "Green Orb",
  };

  const assignedIds = Object.values(matchPowers);
  const availablePowers = powers.filter((p) => !assignedIds.includes(p.id));
  const powerById = (id: string) => powers.find((p) => p.id === id) ?? null;

  const shownMatches = schedule
    .map((m, i) => ({ m, i }))
    .filter(({ m }) => (matchFilter === "upcoming" ? !m.due : m.due));

  // Résultat d'un match : MÊME source que le classement (round-robin figé),
  // indexé par journée. `schedule[i].day` = numéro de journée (round).
  const resultForIndex = (i: number): "win" | "draw" | "loss" | null => {
    const day = schedule[i]?.day;
    return day != null ? resultByRound[day] ?? null : null;
  };

  const handleUpgradeGk = async () => {
    if (wallet < GK_UPGRADE_COST) return;
    setGkUpgrading(true);
    await upgradeGoalkeeper(GK_UPGRADE_COST);
    setGkUpgrading(false);
  };

  const handleToggleLock = () => {
    const next = !locked;
    onSetLocked(next);
    if (next) onToast("Shop rotation locked until tomorrow");
  };

  // ── Feuille de match (page dédiée) ──────────────────────────────────────
  if (sheetFor != null && schedule[sheetFor]) {
    const m = schedule[sheetFor];
    const day = m.day;
    const assignedId = matchPowers[sheetFor];
    const fx = fixtureByRound[day];
    return (
      <MatchSheetPage
        homeName="Mon Équipe"
        opponentName={m.opponentName}
        scheduledAt={m.scheduledAt}
        result={resultByRound[day] ?? null}
        score={scoreByRound[day] ?? null}
        myTeamPower={myTeamPower}
        oppPower={m.oppPower}
        oppSeed={m.opponentSeed}
        goalkeeperLevel={goalkeeperLevel}
        assignedPower={assignedId ? powerById(assignedId) : null}
        realStats={fx?.stats}
        events={fx?.events}
        onBack={() => setSheetFor(null)}
      />
    );
  }

  // ── Bac à sable de test (réservé au testeur) ────────────────────────────
  if (testOpen && runTestMatch) {
    return (
      <MatchSheetPage
        homeName="Mon Équipe"
        opponentName="Sparring Bot"
        scheduledAt={new Date().toISOString()}
        result={null}
        score={null}
        myTeamPower={myTeamPower}
        oppPower={0}
        oppSeed={1}
        goalkeeperLevel={goalkeeperLevel}
        assignedPower={null}
        testMode
        powers={powers}
        onRunTest={runTestMatch}
        onBack={() => setTestOpen(false)}
      />
    );
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
      {orbInspect && (() => {
        const type = orbInspect;
        const cost = ORB_COST[type];
        const canBuy = leagueGold >= cost;
        const orbDesc: Record<AuraType, string> = {
          ROUGE: "Combines with Blue to forge aggressive powers. Boosts precision and attack.",
          BLEUE: "Combines with Red to forge tactical powers. Improves defense and buildups.",
          VERTE: "Rare orb. Required for premium power recipes.",
        };
        return (
          <div
            onClick={() => setOrbInspect(null)}
            style={{
              position: "fixed", inset: 0,
              background: "rgba(0,0,0,0.85)",
              display: "flex", alignItems: "center", justifyContent: "center",
              zIndex: 9999, padding: 20,
            }}
          >
            <div
              onClick={(e) => e.stopPropagation()}
              style={{
                position: "relative",
                background: C.surface,
                borderRadius: 22,
                border: "1.5px solid rgba(255,255,255,0.12)",
                width: "100%", maxWidth: 320,
                padding: "28px 22px",
                textAlign: "center",
              }}
            >
              <button
                onClick={() => setOrbInspect(null)}
                style={{
                  position: "absolute", top: 12, right: 12,
                  width: 30, height: 30, borderRadius: 9,
                  background: "rgba(255,255,255,0.06)",
                  border: "1px solid rgba(255,255,255,0.1)",
                  color: C.muted, fontSize: 16, fontWeight: 900,
                  cursor: "pointer", display: "grid", placeItems: "center",
                }}
              >✕</button>
              <div style={{ display: "flex", justifyContent: "center", marginBottom: 16 }}>
                <OrbIcon type={type} size={64} />
              </div>
              <div
                style={{
                  display: "inline-block",
                  fontSize: 10, fontWeight: 900,
                  color: C.muted,
                  background: "rgba(255,255,255,0.06)",
                  borderRadius: 5, padding: "2px 8px",
                  marginBottom: 10,
                }}
              >Orb</div>
              <div style={{ fontSize: 18, fontWeight: 900, marginBottom: 8 }}>
                {ORB_LABEL[type]}
              </div>
              <div style={{ fontSize: 13, color: C.muted, lineHeight: 1.5, marginBottom: 20 }}>
                {orbDesc[type]}
              </div>
              <button
                onClick={() => {
                  if (onBuyOrb(type)) { onToast(`${ORB_LABEL[type]} purchased`); setOrbInspect(null); }
                  else onToast("Not enough coins");
                }}
                disabled={!canBuy}
                style={{
                  width: "100%",
                  background: canBuy ? C.turf : "rgba(255,255,255,0.05)",
                  color: canBuy ? "#050f0a" : "rgba(255,255,255,0.25)",
                  border: "none", borderRadius: 12,
                  padding: "13px", fontSize: 15, fontWeight: 900,
                  cursor: canBuy ? "pointer" : "not-allowed",
                }}
              >
                {canBuy ? `Buy — 💰 ${cost}` : `Not enough coins (💰 ${cost})`}
              </button>
            </div>
          </div>
        );
      })()}

      {inspect && (
        <CardModal
          card={inspect.card}
          onClose={() => setInspect(null)}
          buy={
            inspect.buyable
              ? {
                  gold: leagueGold,
                  onBuy: () => onBuy(inspect.card.id),
                  full: invFull,
                }
              : undefined
          }
          sell={
            !inspect.buyable
              ? {
                  refund: SELL_REFUND[inspect.card.tier],
                  onSell: () => {
                    const refund = SELL_REFUND[inspect.card.tier];
                    onSell(inspect.card.id);
                    onToast(refund > 0 ? `Sold · +${refund} 💰` : "Sold (Common · Free)");
                  },
                }
              : undefined
          }
        />
      )}
      {familyModal && (
        <FamilyModal family={familyModal} onClose={() => setFamilyModal(null)} />
      )}
      {inspectPower && (
        <div
          onClick={() => setInspectPower(null)}
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.85)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999,
            padding: 20,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: C.surface,
              borderRadius: 22,
              border: "1.5px solid rgba(168,85,247,0.4)",
              width: "100%",
              maxWidth: 340,
              padding: "26px 22px",
              textAlign: "center",
            }}
          >
            <div
              style={{
                width: 76,
                height: 76,
                margin: "0 auto 14px",
                borderRadius: 18,
                background:
                  "linear-gradient(160deg,rgba(168,85,247,0.25),rgba(168,85,247,0.08))",
                border: "1.5px solid rgba(168,85,247,0.5)",
                display: "grid",
                placeItems: "center",
                fontSize: 38,
              }}
            >
              {EFFECT_ICONS[inspectPower.effectType] ?? "✨"}
            </div>
            <div style={{ fontSize: 19, fontWeight: 900 }}>
              {inspectPower.name}
              {inspectPower.level && inspectPower.level > 1 && (
                <span
                  style={{ fontSize: 12, fontWeight: 900, color: C.gold, marginLeft: 8 }}
                >
                  Lv. {inspectPower.level}
                </span>
              )}
            </div>
            <div
              style={{
                fontSize: 13,
                color: C.muted,
                lineHeight: 1.5,
                margin: "8px 0 18px",
              }}
            >
              {powerDesc(inspectPower)}
            </div>
            <button
              onClick={() => setInspectPower(null)}
              style={{
                background: C.turf,
                color: "#050f0a",
                border: "none",
                borderRadius: 12,
                padding: "12px 28px",
                fontSize: 14,
                fontWeight: 900,
                cursor: "pointer",
              }}
            >
              Close
            </button>
          </div>
        </div>
      )}

      {/* Back */}
      <button
        onClick={onBack}
        style={{
          alignSelf: "flex-start",
          background: "transparent",
          border: "none",
          color: C.muted,
          fontSize: 13,
          fontWeight: 700,
          cursor: "pointer",
          padding: "4px 0",
        }}
      >
        ← League Overview
      </button>

      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <h2
          style={{ margin: 0, fontSize: 22, fontWeight: 900, letterSpacing: -0.5 }}
        >
          Match Preparation
        </h2>
        <span
          style={{ fontSize: 16, fontWeight: 900, color: "#7DD3FC", whiteSpace: "nowrap" }}
          title="League Golds"
        >
          💰 {leagueGold}
        </span>
      </div>

      {/* ─── Boutique Quotidienne ─── */}
      <div>
        <div
          style={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            gap: 10,
            marginBottom: 10,
          }}
        >
          <div>
            <h3 style={{ ...sectionTitle, margin: 0 }}>Daily Shop</h3>
            <ResetCountdown locked={locked} />
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            {/* Verrou de rotation (carré) */}
            <button
              onClick={handleToggleLock}
              title={
                locked
                  ? "Rotation locked — tap to unlock"
                  : "Lock rotation until tomorrow"
              }
              style={{
                width: 40,
                height: 40,
                borderRadius: 11,
                display: "grid",
                placeItems: "center",
                cursor: "pointer",
                background: locked
                  ? "rgba(45,227,107,0.12)"
                  : "rgba(255,255,255,0.04)",
                border: `1.5px solid ${
                  locked ? C.turf : "rgba(255,255,255,0.14)"
                }`,
                color: locked ? C.turf : C.muted,
                transition: "all .15s",
              }}
            >
              <LockIcon open={!locked} size={20} />
            </button>
            {/* Reroll (sans bordure de couleur) */}
            <button
              onClick={onReroll}
              disabled={leagueGold < rerollCost || locked}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 5,
                background:
                  leagueGold >= rerollCost && !locked
                    ? "linear-gradient(135deg,#0e2236,#0a1626)"
                    : "rgba(255,255,255,0.04)",
                border: "none",
                borderRadius: 10,
                padding: "10px 14px",
                fontSize: 12.5,
                fontWeight: 900,
                color:
                  leagueGold >= rerollCost && !locked
                    ? "#7DD3FC"
                    : "rgba(255,255,255,0.25)",
                cursor:
                  leagueGold >= rerollCost && !locked ? "pointer" : "not-allowed",
                whiteSpace: "nowrap",
              }}
            >
              🔄 Reroll · {rerollCost === 0 ? "FREE" : `${rerollCost} 💰`}
            </button>
          </div>
        </div>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(3, 1fr)",
            gap: 8,
          }}
        >
          {shop.offers.map((id, idx) => {
            const card = id ? CARD_BY_ID[id] : null;
            if (!card) return <ShopCardPlaceholder key={`empty-${idx}`} />;
            return (
              <ShopCard
                key={`${id}-${idx}`}
                card={card}
                onInspect={(c) => setInspect({ card: c, buyable: true })}
                onFamilyClick={setFamilyModal}
              />
            );
          })}

          {/* ─── Orbs (rouge / bleue / verte) directement dans le shop ─── */}
          {ALL_AURA_TYPES.map((type) => {
            const cost = ORB_COST[type];
            return (
              <div
                key={type}
                role="button"
                tabIndex={0}
                onClick={() => setOrbInspect(type)}
                style={{
                  background: C.surface,
                  border: "1.5px solid rgba(255,255,255,0.14)",
                  borderRadius: 16,
                  padding: "10px 10px 0",
                  display: "flex",
                  flexDirection: "column",
                  minWidth: 0,
                  cursor: "pointer",
                  minHeight: 124,
                }}
              >
                {/* Icône (centrée) */}
                <div
                  style={{
                    alignSelf: "center",
                    marginTop: 4,
                    height: 42,
                    display: "grid",
                    placeItems: "center",
                  }}
                >
                  <OrbIcon type={type} size={34} />
                </div>

                {/* Nom (centré) */}
                <span
                  style={{
                    fontSize: 12,
                    fontWeight: 900,
                    color: C.text,
                    lineHeight: 1.15,
                    minHeight: 28,
                    textAlign: "center",
                  }}
                >
                  {ORB_LABEL[type]}
                </span>

                {/* Texte secondaire (à la place des combos) */}
                <div
                  style={{
                    padding: "4px 0 2px",
                    fontSize: 9.5,
                    fontWeight: 700,
                    color: C.faint,
                    textAlign: "center",
                  }}
                >
                  Boost fuel
                </div>

                {/* Séparateur */}
                <div
                  style={{
                    height: 1,
                    background: "rgba(255,255,255,0.1)",
                    margin: "4px -10px 0",
                  }}
                />

                {/* Tag "Orb" (bas gauche) + prix (bas droite) */}
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    padding: "8px 0",
                    gap: 4,
                  }}
                >
                  <span
                    style={{
                      fontSize: 10,
                      fontWeight: 900,
                      color: C.muted,
                      background: "rgba(255,255,255,0.06)",
                      borderRadius: 5,
                      padding: "2px 6px",
                    }}
                  >
                    Orb
                  </span>
                  <span
                    style={{
                      fontSize: 14,
                      fontWeight: 900,
                      color: C.text,
                      whiteSpace: "nowrap",
                    }}
                  >
                    💰 {cost}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* ─── Inventaire (liste) ─── */}
      <div>
        <div
          style={{
            display: "flex",
            alignItems: "baseline",
            gap: 10,
            marginBottom: 10,
            flexWrap: "wrap",
          }}
        >
          <h3 style={{ ...sectionTitle, margin: 0 }}>Inventory</h3>
          <span style={{ fontSize: 11.5, color: C.muted }}>
            You can only send one asset to the field at a time
          </span>
        </div>

        {ownedIds.length === 0 ? (
          <div
            style={{
              textAlign: "center",
              fontSize: 12,
              color: C.muted,
              padding: "24px 0",
              background: C.surface,
              borderRadius: 14,
              border: "1px dashed rgba(255,255,255,0.1)",
            }}
          >
            No cards. Buy some from the shop above.
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {ownedIds.map((id) => {
              const card = CARD_BY_ID[id];
              if (!card) return null;
              const tier = TIER_META[card.tier];
              const isSent = myCard === id;
              const qty = inventory[id];
              return (
                <div
                  key={id}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                    background: C.surface,
                    border: `1px solid ${
                      isSent ? "rgba(45,227,107,0.35)" : "rgba(255,255,255,0.06)"
                    }`,
                    borderRadius: 12,
                    padding: "8px 10px",
                  }}
                >
                  <button
                    onClick={() => setInspect({ card, buyable: false })}
                    style={{
                      width: 42,
                      height: 42,
                      borderRadius: 10,
                      background: `linear-gradient(160deg,${tier.color}33,${tier.color}10)`,
                      border: `1.5px solid ${tier.color}`,
                      display: "grid",
                      placeItems: "center",
                      fontSize: 22,
                      flexShrink: 0,
                      cursor: "pointer",
                    }}
                  >
                    {card.icon}
                  </button>
                  <div
                    style={{ minWidth: 0, flex: 1, cursor: "pointer" }}
                    onClick={() => setInspect({ card, buyable: false })}
                  >
                    <div
                      style={{
                        fontSize: 13,
                        fontWeight: 800,
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {card.name}
                      {qty > 1 && (
                        <span style={{ color: C.faint, fontWeight: 700 }}> ×{qty}</span>
                      )}
                    </div>
                    <div style={{ fontSize: 11, fontWeight: 800, color: tier.color }}>
                      {tier.label}
                    </div>
                  </div>
                  <FamilyDots card={card} size={18} onFamilyClick={setFamilyModal} />
                  <button
                    onClick={() => onSendToField(id)}
                    style={{
                      flexShrink: 0,
                      width: 152,
                      textAlign: "center",
                      background: isSent
                        ? "rgba(45,227,107,0.12)"
                        : "rgba(255,255,255,0.05)",
                      border: `1px solid ${
                        isSent ? C.turf : "rgba(255,255,255,0.12)"
                      }`,
                      borderRadius: 9,
                      padding: "8px 12px",
                      fontSize: 12,
                      fontWeight: 800,
                      color: isSent ? C.turf : C.text,
                      cursor: "pointer",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {isSent ? "✅ On the field" : "Send to field"}
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* ─── Boost (orbes + pouvoirs) ─── */}
      <div>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 10,
            marginBottom: 10,
          }}
        >
          <h3 style={{ ...sectionTitle, margin: 0 }}>Boost</h3>
          <div style={{ display: "flex", gap: 10 }}>
            {ALL_AURA_TYPES.map((t) => (
              <OrbIcon
                key={t}
                type={t}
                size={34}
                count={orbCounts[t]}
                dim={orbCounts[t] === 0}
              />
            ))}
          </div>
        </div>

        {/* Boosts (cliquables) + placeholders */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(5, 1fr)",
            gap: 8,
            marginBottom: 10,
          }}
        >
          {powers.map((p) => {
            const isAssigned = assignedIds.includes(p.id);
            return (
              <button
                key={p.id}
                onClick={() => !isAssigned && setInspectPower(p)}
                title={isAssigned ? "Assigned to a match" : `${p.name}${p.level && p.level > 1 ? ` (Lv. ${p.level})` : ""}`}
                style={{
                  position: "relative",
                  aspectRatio: "1",
                  background: isAssigned
                    ? "rgba(255,255,255,0.03)"
                    : "linear-gradient(160deg,rgba(168,85,247,0.18),rgba(168,85,247,0.06))",
                  border: isAssigned
                    ? "1.5px dashed rgba(255,255,255,0.12)"
                    : "1.5px solid rgba(168,85,247,0.5)",
                  borderRadius: 12,
                  display: "grid",
                  placeItems: "center",
                  fontSize: 22,
                  cursor: isAssigned ? "default" : "pointer",
                  opacity: isAssigned ? 0.35 : 1,
                  filter: isAssigned ? "grayscale(1)" : "none",
                }}
              >
                {EFFECT_ICONS[p.effectType] ?? "✨"}
                {p.level && p.level > 1 && !isAssigned && (
                  <span
                    style={{
                      position: "absolute",
                      bottom: 2,
                      right: 4,
                      fontSize: 9,
                      fontWeight: 900,
                      color: C.gold,
                    }}
                  >
                    Lv{p.level}
                  </span>
                )}
              </button>
            );
          })}
          {Array.from({ length: Math.max(0, 5 - powers.length) }).map((_, i) => (
            <div
              key={`ph-${i}`}
              style={{
                aspectRatio: "1",
                background: "rgba(255,255,255,0.03)",
                border: "1.5px dashed rgba(255,255,255,0.08)",
                borderRadius: 12,
              }}
            />
          ))}
        </div>

        <button
          onClick={onFusion}
          style={{
            width: "100%",
            background: "linear-gradient(135deg,rgba(168,85,247,0.18),rgba(168,85,247,0.08))",
            border: "1.5px solid rgba(168,85,247,0.45)",
            borderRadius: 12,
            color: "#d8b4fe",
            fontSize: 13,
            fontWeight: 900,
            cursor: "pointer",
            padding: "12px 16px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 8,
            letterSpacing: 0.3,
          }}
        >
          <span style={{ fontSize: 16 }}>✨</span>
          Craft Boost
        </button>
      </div>

      {/* ─── Matchs ─── */}
      <div>
        <h3 style={sectionTitle}>Matches</h3>
        <div
          style={{
            display: "inline-flex",
            gap: 4,
            background: C.surface2,
            borderRadius: 999,
            padding: 4,
            border: "1px solid rgba(255,255,255,0.06)",
            marginBottom: 10,
          }}
        >
          {(
            [
              ["upcoming", "Upcoming"],
              ["done", "Finished"],
            ] as const
          ).map(([id, label]) => (
            <button
              key={id}
              onClick={() => {
                const y = window.scrollY;
                flushSync(() => {
                  setMatchFilter(id);
                  setPickerFor(null);
                  setDetailFor(null);
                });
                window.scrollTo(0, y);
              }}
              style={{
                background: matchFilter === id ? C.surface : "transparent",
                border:
                  matchFilter === id
                    ? "1px solid rgba(255,255,255,0.12)"
                    : "1px solid transparent",
                borderRadius: 999,
                padding: "7px 16px",
                fontSize: 12.5,
                fontWeight: matchFilter === id ? 800 : 600,
                color: matchFilter === id ? C.text : C.muted,
                cursor: "pointer",
              }}
            >
              {label}
            </button>
          ))}
        </div>

        {isTester && matchFilter === "done" && runTestMatch && (
          <button
            onClick={() => setTestOpen(true)}
            style={{
              width: "100%",
              textAlign: "left",
              background: "rgba(168,85,247,0.10)",
              border: "1px solid rgba(168,85,247,0.4)",
              borderRadius: 12,
              padding: "10px 12px",
              marginBottom: 8,
              cursor: "pointer",
              color: C.text,
            }}
          >
            <div style={{ fontSize: 11, fontWeight: 800, color: "#C084FC" }}>
              SANDBOX
            </div>
            <div style={{ fontSize: 13, fontWeight: 700 }}>
              🧪 Match Test — moteur de simulation
            </div>
          </button>
        )}

        {shownMatches.length === 0 ? (
          <div
            style={{
              textAlign: "center",
              fontSize: 12,
              color: C.muted,
              padding: "20px 0",
              background: C.surface,
              borderRadius: 14,
              border: "1px dashed rgba(255,255,255,0.1)",
            }}
          >
            {matchFilter === "upcoming"
              ? "All matches this week are done."
              : "No matches played yet."}
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {shownMatches.map(({ m, i }) => {
              const assignedId = matchPowers[i];
              const assigned = assignedId ? powerById(assignedId) : null;
              const isPickerOpen = pickerFor === i;
              const isDetailOpen = detailFor === i;
              return (
                <div
                  key={i}
                  style={{
                    background: C.surface,
                    border: `1px solid ${
                      assigned ? "rgba(45,227,107,0.3)" : "rgba(255,255,255,0.06)"
                    }`,
                    borderRadius: 12,
                    overflow: "hidden",
                    opacity: m.due ? 0.7 : 1,
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                      padding: "10px 12px",
                    }}
                  >
                    <div style={{ minWidth: 0, flex: 1 }}>
                      <div
                        style={{
                          fontSize: 11,
                          fontWeight: 800,
                          color: C.muted,
                          textTransform: "capitalize",
                        }}
                      >
                        {new Date(m.scheduledAt).toLocaleDateString("en-US", {
                          weekday: "long",
                        })}
                      </div>
                      <div
                        style={{
                          fontSize: 13,
                          fontWeight: 700,
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        {m.opponentName}
                      </div>
                    </div>

                    {m.due ? (() => {
                      const res = resultForIndex(i);
                      const cfg = {
                        win:  { label: "Win",  bg: "rgba(45,227,107,0.12)", border: "rgba(45,227,107,0.4)",  color: C.turf },
                        draw: { label: "Draw", bg: "rgba(251,191,36,0.12)", border: "rgba(251,191,36,0.4)",  color: "#FBB824" },
                        loss: { label: "Loss", bg: "rgba(255,80,80,0.10)",  border: "rgba(255,80,80,0.35)",  color: "#ff6b6b" },
                      };
                      const c = res ? cfg[res] : null;
                      return c ? (
                        <div style={{
                          flexShrink: 0,
                          background: c.bg,
                          border: `1px solid ${c.border}`,
                          borderRadius: 8,
                          padding: "7px 12px",
                          fontSize: 11.5,
                          fontWeight: 900,
                          color: c.color,
                        }}>
                          {c.label}
                        </div>
                      ) : null;
                    })() : (
                      <button
                        onClick={() =>
                          assigned
                            ? unassignMatch(i)
                            : setPickerFor(isPickerOpen ? null : i)
                        }
                        style={{
                          flexShrink: 0,
                          background: assigned
                            ? "rgba(45,227,107,0.12)"
                            : "rgba(255,255,255,0.05)",
                          border: `1px solid ${
                            assigned ? "rgba(45,227,107,0.4)" : "rgba(255,255,255,0.12)"
                          }`,
                          borderRadius: 8,
                          padding: "7px 10px",
                          fontSize: 11.5,
                          fontWeight: 800,
                          color: assigned ? C.turf : C.muted,
                          cursor: "pointer",
                          maxWidth: 150,
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        {assigned
                          ? `${EFFECT_ICONS[assigned.effectType] ?? "✨"} ${assigned.name} ✕`
                          : "Add a boost"}
                      </button>
                    )}

                    <button
                      onClick={() =>
                        m.due
                          ? setSheetFor(i)
                          : setDetailFor(isDetailOpen ? null : i)
                      }
                      style={{
                        flexShrink: 0,
                        background: "transparent",
                        border: "1px solid rgba(255,255,255,0.1)",
                        borderRadius: 8,
                        padding: "7px 10px",
                        fontSize: 11.5,
                        fontWeight: 700,
                        color: C.muted,
                        cursor: "pointer",
                      }}
                    >
                      Détails
                    </button>
                  </div>

                  {isPickerOpen && !assigned && !m.due && (
                    <div
                      style={{
                        borderTop: "1px solid rgba(255,255,255,0.05)",
                        padding: "10px 12px",
                        display: "flex",
                        flexDirection: "column",
                        gap: 6,
                      }}
                    >
                      {availablePowers.length === 0 ? (
                        <div style={{ fontSize: 12, color: C.muted }}>
                          No power available — craft a boost first.
                        </div>
                      ) : (
                        availablePowers.map((p) => (
                          <button
                            key={p.id}
                            onClick={() => {
                              assignPowerToMatch(i, p.id);
                              setPickerFor(null);
                            }}
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: 8,
                              background: "rgba(255,255,255,0.04)",
                              border: "1px solid rgba(255,255,255,0.08)",
                              borderRadius: 9,
                              padding: "8px 10px",
                              fontSize: 12.5,
                              fontWeight: 700,
                              color: C.text,
                              cursor: "pointer",
                              textAlign: "left",
                            }}
                          >
                            <span style={{ fontSize: 16 }}>
                              {EFFECT_ICONS[p.effectType] ?? "✨"}
                            </span>
                            {p.name}
                          </button>
                        ))
                      )}
                    </div>
                  )}

                  {isDetailOpen && !m.due && (
                    <div
                      style={{
                        borderTop: "1px solid rgba(255,255,255,0.05)",
                        padding: "10px 12px",
                        display: "flex",
                        gap: 16,
                        fontSize: 12,
                        flexWrap: "wrap",
                      }}
                    >
                      <div>
                        <span style={{ color: C.muted }}>Puissance adverse </span>
                        <span style={{ color: C.live, fontWeight: 800 }}>
                          ⚡ {m.oppPower}
                        </span>
                      </div>
                      <div>
                        <span style={{ color: C.muted }}>Coup d'envoi </span>
                        <span style={{ fontWeight: 800 }}>
                          {new Date(m.scheduledAt).toLocaleDateString("en-US", {
                            weekday: "short",
                            day: "numeric",
                            month: "short",
                          })}{" "}
                          16:00
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* ─── Gardien ─── */}
      <div>
        <h3 style={sectionTitle}>🧤 Goalkeeper</h3>
        <div
          style={{
            background: C.surface,
            border: "1px solid rgba(59,130,246,0.2)",
            borderRadius: 14,
            padding: "16px",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              marginBottom: 14,
            }}
          >
            <div>
              <div style={{ fontSize: 15, fontWeight: 800 }}>
                Level {goalkeeperLevel}
              </div>
              <div style={{ fontSize: 12, color: C.muted, marginTop: 2 }}>
                +{goalkeeperLevel * 20} passive defense
              </div>
            </div>
            <div style={{ display: "flex", gap: 5 }}>
              {Array.from({ length: 5 }).map((_, i) => (
                <div
                  key={i}
                  style={{
                    width: 12,
                    height: 12,
                    borderRadius: 3,
                    background:
                      i < goalkeeperLevel ? "#3B82F6" : "rgba(255,255,255,0.08)",
                  }}
                />
              ))}
            </div>
          </div>
          <button
            onClick={handleUpgradeGk}
            disabled={wallet < GK_UPGRADE_COST || gkUpgrading}
            style={{
              width: "100%",
              background:
                wallet >= GK_UPGRADE_COST
                  ? "rgba(59,130,246,0.15)"
                  : "rgba(255,255,255,0.04)",
              border: `1px solid ${
                wallet >= GK_UPGRADE_COST
                  ? "rgba(59,130,246,0.4)"
                  : "rgba(255,255,255,0.08)"
              }`,
              color: wallet >= GK_UPGRADE_COST ? "#3B82F6" : "rgba(255,255,255,0.2)",
              borderRadius: 10,
              padding: "12px",
              fontSize: 13,
              fontWeight: 800,
              cursor: wallet >= GK_UPGRADE_COST ? "pointer" : "not-allowed",
            }}
          >
            {gkUpgrading ? "Upgrading…" : `Upgrade — ${GK_UPGRADE_COST} 🪙`}
          </button>
          {wallet < GK_UPGRADE_COST && (
            <div
              style={{
                fontSize: 11,
                color: C.muted,
                textAlign: "center",
                marginTop: 8,
              }}
            >
              Not enough coins ({wallet} / {GK_UPGRADE_COST} 🪙)
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
